import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Select from "../../ui/PortalSelect";
import { DatePicker, Spin, Tooltip as AntTooltip, Empty, Drawer, Space, Statistic, Tag } from "antd";
import Swal from "sweetalert2";
import moment from "moment";
import axios from "axios";
import { baseURL } from "../backend_url";
import Papa from "papaparse";

import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip,
  Menu,
  MenuItem,
  Switch,
  Alert,
  Collapse,
} from "@mui/material";

import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { loadAccessibleCompanies, readSessionUser } from '../accountAccess';

const { RangePicker } = DatePicker;

/**
 * Header mapping with improved organization
 */
const RESPONSE_HEADER_MAPPING = {
  locationName: "Location Name",
  pStartDate: "Pickup Date",
  pLor: "LOR",
  pSippCode: "Routes SIPP Code",
  pCompSippCode: "Competitor SIPP Code",
  pCompetitorName: "Competitor Name",
  pCompetitorPrice: "Competitor Price",
  pRangeValue: "Range Value",
  pStrategy: "Strategy",
  pMinimumRate: "Is From Minimum Rate Mapping",
};

const getFriendlyHeader = (key) => RESPONSE_HEADER_MAPPING[key] || key;

/**
 * Enhanced select styles with improved UX
 */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 48,
    borderRadius: 12,
    borderColor: state.isFocused ? "#3f51b5" : "rgba(0,0,0,0.12)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(63,81,181,0.1)" : "none",
    backgroundColor: state.isDisabled ? "rgba(0,0,0,0.04)" : "white",
    transition: "all 200ms ease",
    ":hover": { borderColor: "#3f51b5" },
  }),
  valueContainer: (base) => ({ ...base, padding: "6px 14px" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.95rem",
  }),
  placeholder: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#999",
  }),
  menu: (base) => ({ ...base, borderRadius: 12, overflow: "hidden", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: state.isSelected ? "#3f51b5" : state.isFocused ? "rgba(63,81,181,0.1)" : "white",
    color: state.isSelected ? "white" : "black",
    transition: "all 150ms ease",
  }),
};

const formatMomentToISOZ = (m) => (m ? moment(m).toISOString() : "");

/**
 * Convert pFinalArray to rows
 */
function buildFinalArrayRows(pFinalArray) {
  if (!pFinalArray || typeof pFinalArray !== "object") return [];

  const keys = Object.keys(pFinalArray);
  if (!keys.length) return [];

  let maxLen = 0;
  for (const k of keys) {
    const v = pFinalArray[k];
    if (Array.isArray(v)) maxLen = Math.max(maxLen, v.length);
  }

  if (maxLen === 0) return [pFinalArray];

  const rows = [];
  for (let i = 0; i < maxLen; i++) {
    const row = {};
    for (const k of keys) {
      const v = pFinalArray[k];
      row[k] = Array.isArray(v) ? v[i] : v;
    }
    rows.push(row);
  }
  return rows;
}

function TabPanel({ value, index, children }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
      {value === index ? children : null}
    </Box>
  );
}

/**
 * Statistics Card Component
 */
function StatCard({ label, value, icon: Icon, color = "#3f51b5", trend, sublabel }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid rgba(0,0,0,0.08)",
        background: `linear-gradient(135deg, ${color}08 0%, transparent 100%)`,
        transition: "all 200ms ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          borderColor: color,
        },
      }}
    >
      <CardContent>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: `${color}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ color, fontSize: 24 }} />
            </Box>
            {trend && (
              <Chip
                label={trend}
                size="small"
                color={trend.includes("+") ? "success" : "error"}
                variant="outlined"
              />
            )}
          </Stack>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color }}>
              {value}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {label}
            </Typography>
            {sublabel && (
              <Typography variant="caption" sx={{ display: "block", opacity: 0.5, mt: 0.5 }}>
                {sublabel}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * Main Component
 */
export default function EnhancedAutomationScheduler() {
  // State management
  const [companyDetail, setcompanyDetail] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [company_id, setcompany_id] = useState("");

  const [loggedInUserDetail, setLoggedInUserDetail] = useState({});

  const [locationName, setLocationName] = useState("");
  const [locationDetail, setLocationDetail] = useState([{}]);
  const [locationID, setLocationID] = useState("");

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [tableData, setTableData] = useState([]);
  const [originalData, setOriginalData] = useState([]);

  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSort, setSelectedSort] = useState("newest");
  const [expandFilters, setExpandFilters] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;

  // View dialog
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [viewTab, setViewTab] = useState(0);

  // Advanced features
  const [showStats, setShowStats] = useState(true);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [anchorEl, setAnchorEl] = useState(null);

  // Load user + companies
  useEffect(() => {
    const { user: foundUser, error } = readSessionUser();
    if (!foundUser) {
      setcompanyDetail([]);
      setLoadingCompanies(false);
      Swal.fire(error);
      return;
    }

    setLoggedInUserDetail(foundUser);

    const loadCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const accounts = await loadAccessibleCompanies(
          baseURL,
          foundUser,
          "Adjustment_Account Access",
          { method: "GET", headers: { "Content-Type": "application/json" } }
        );
        setcompanyDetail(accounts);
      } catch (e) {
        setcompanyDetail([]);
        Swal.fire("Error loading accounts");
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, []);

  // Load locations when company changes
  useEffect(() => {
    const loadLocations = async () => {
      if (!companyDetail || !companyName) return;

      setTableData([]);
      setOriginalData([]);
      setSearchQuery("");
      setCurrentPage(1);
      setSelectedRows(new Set());

      setLocationName("");
      setLocationID("");
      setLocationDetail([]);

      setLoadingLocations(true);

      try {
        const obj = companyDetail.find((v) => v.company_name === companyName);
        if (!obj) return;

        const company_id_temp = obj._id;
        setcompany_id(company_id_temp);

        const res = await axios.get(
          baseURL + "/get_company_location/" + company_id_temp
          + "?companyNumber=" + encodeURIComponent(company_id_temp.toString())
        );
        let allTemp = res?.data?.res || [];

        if (loggedInUserDetail.role_capabilities?.all_modules_access !== true) {
          try {
            const userRes = await axios.post(baseURL + "/get_individual_user_details", {
              companyNumber: company_id_temp.toString(),
              user_email: loggedInUserDetail.userEmail,
            });
            const user = userRes?.data?.[0];
            if (user?.location_allocated) {
              allTemp = allTemp.filter((loc) => user.location_allocated[loc.location_id]);
            }
          } catch (e) {
            console.warn("User access filter failed", e);
          }
        }

        setLocationDetail(allTemp);
      } catch (e) {
        console.error(e);
        Swal.fire("Error loading locations");
      } finally {
        setLoadingLocations(false);
      }
    };

    loadLocations();
  }, [companyName, loggedInUserDetail]);

  // locationName -> locationID
  useEffect(() => {
    if (!locationName || !locationDetail?.length) {
      setLocationID("");
      return;
    }
    const id = locationDetail.find((loc) => loc.location_name === locationName)?.location_id;
    setLocationID(id || "");
  }, [locationName, locationDetail]);

  // Options
  const companyNameOptions = useMemo(
    () => (companyDetail || []).filter(Boolean).map((v) => ({ value: v.company_name, label: v.company_name })),
    [companyDetail]
  );

  const locationNameOptions = useMemo(
    () => (locationDetail || []).filter(Boolean).map((v) => ({ value: v.location_name, label: v.location_name })),
    [locationDetail]
  );

  // Validation
  const validateForm = () => {
    if (!companyName) {
      Swal.fire("Please select an account to proceed ahead");
      return false;
    }
    if (!locationName) {
      Swal.fire("Please select a location to proceed ahead");
      return false;
    }
    if (!fromDate || !toDate) {
      Swal.fire("Please select a date range to proceed ahead");
      return false;
    }
    if (moment(fromDate).isAfter(moment(toDate))) {
      Swal.fire("From Date can not be greater than To Date");
      return false;
    }
    return true;
  };

  // Fetch jobs
  const handleFetch = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoadingTable(true);
    try {
      const payload = {
        companyNumber: company_id?.toString(),
        account_id: company_id?.toString(),
        location_name: locationID,
        from_date: formatMomentToISOZ(fromDate),
        to_date: formatMomentToISOZ(toDate),
      };

      const res = await axios.post(baseURL + "/get_automation_scheduler_jobs", payload);
      const data = res.data || [];

      setTableData(data);
      setOriginalData(data);
      setCurrentPage(1);
      setSelectedRows(new Set());

      if (!data.length) {
        Swal.fire({
          icon: "info",
          title: "No Jobs found",
          text: "Try expanding the date range or choosing another location.",
        });
      }
    } catch (e) {
      console.error(e);
      Swal.fire("Error fetching jobs");
    } finally {
      setLoadingTable(false);
    }
  };

  // Sort logic
  const sortedData = useCallback(() => {
    let sorted = [...tableData];
    switch (selectedSort) {
      case "newest":
        sorted.sort((a, b) => new Date(b.created_on || b.created_date) - new Date(a.created_on || a.created_date));
        break;
      case "oldest":
        sorted.sort((a, b) => new Date(a.created_on || a.created_date) - new Date(b.created_on || b.created_date));
        break;
      case "nameAZ":
        sorted.sort((a, b) => (a.automationName || "").localeCompare(b.automationName || ""));
        break;
      case "nameZA":
        sorted.sort((a, b) => (b.automationName || "").localeCompare(a.automationName || ""));
        break;
      default:
        break;
    }
    return sorted;
  }, [tableData, selectedSort]);

  // Search & sort combined
  useEffect(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    let filtered = originalData || [];

    if (q) {
      filtered = filtered.filter((row) => {
        const automation = (row?.automationName || "").toLowerCase();
        const loc = (row?.location_name || "").toLowerCase();
        const createdBy = (row?.created_by || "").toLowerCase();
        const createdOn = (row?.created_on || row?.created_date || "").toLowerCase();
        return automation.includes(q) || loc.includes(q) || createdBy.includes(q) || createdOn.includes(q);
      });
    }

    setTableData(filtered);
    setCurrentPage(1);
  }, [searchQuery, originalData]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil((tableData?.length || 0) / rowsPerPage));
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData().slice(indexOfFirstRow, indexOfLastRow);

  // Stats
  const stats = useMemo(() => {
    const total = tableData?.length || 0;
    const rangeText =
      fromDate && toDate ? `${moment(fromDate).format("MMM D")} → ${moment(toDate).format("MMM D")}` : "—";
    const avgPerDay = fromDate && toDate ? (total / (moment(toDate).diff(moment(fromDate), "days") + 1)).toFixed(1) : 0;
    return { total, rangeText, avgPerDay };
  }, [tableData, fromDate, toDate]);

  // CSV Download
  const handleDownloadClick = (row) => {
    const arrayLength = Array.isArray(row?.pFinalArray?.pCompSippCode)
      ? row.pFinalArray.pCompSippCode.length
      : 0;

    const formattedData = [];
    for (let i = 0; i < arrayLength; i++) {
      const newItem = {};
      Object.entries(RESPONSE_HEADER_MAPPING).forEach(([oldKey, newHeader]) => {
        let value = row?.pFinalArray?.[oldKey];
        if (Array.isArray(value)) value = value[i];

        if (newHeader === "Competitor Price" && (isNaN(value) || value === undefined || value === null || value === "")) {
          value = "-";
        }
        newItem[newHeader] = value;
      });
      formattedData.push(newItem);
    }

    const csv = Papa.unparse({
      fields: Object.values(RESPONSE_HEADER_MAPPING),
      data: formattedData.map((r) => Object.values(r)),
    });

    const safeCreated = (row?.created_on || row?.created_date || "").toString().replace(/[/:]/g, "-");
    const fileName = `${row?.automationName || "automation"}_${safeCreated}.csv`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Bulk download
  const handleBulkDownload = () => {
    if (selectedRows.size === 0) {
      Swal.fire("Please select at least one job to download");
      return;
    }

    selectedRows.forEach((idx) => {
      const row = tableData[idx];
      setTimeout(() => handleDownloadClick(row), 100);
    });

    setSelectedRows(new Set());
    setBulkSelectMode(false);
    Swal.fire("Downloads initiated");
  };

  const handleReset = () => {
    setCompanyName("");
    setcompany_id("");
    setLocationName("");
    setLocationID("");
    setLocationDetail([]);
    setFromDate(null);
    setToDate(null);
    setTableData([]);
    setOriginalData([]);
    setSearchQuery("");
    setCurrentPage(1);
    setSelectedRows(new Set());
    setBulkSelectMode(false);
  };

  const openView = (row) => {
    setViewRow(row);
    setViewTab(0);
    setViewOpen(true);
  };

  const closeView = () => {
    setViewOpen(false);
    setViewRow(null);
  };

  // Response rows & columns
  const responseRows = useMemo(() => buildFinalArrayRows(viewRow?.pFinalArray), [viewRow]);

  const responseCols = useMemo(() => {
    if (!responseRows?.length) return [];
    const keys = Object.keys(responseRows[0] || {});
    const mappedOrder = Object.keys(RESPONSE_HEADER_MAPPING).filter((k) => keys.includes(k));
    const remaining = keys.filter((k) => !mappedOrder.includes(k));
    return [...mappedOrder, ...remaining];
  }, [responseRows]);

  return (
    <Box sx={{ width: "100%", maxWidth: "none", p: { xs: 1.5, md: 3 }, mx: 0 }}>
      {/* Header Section with Stats */}
      <Stack spacing={2.5} sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                letterSpacing: -0.8,
                background: "linear-gradient(135deg, #3f51b5 0%, #1a237e 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Automation Scheduler
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
              Monitor and manage all your automation jobs
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Toggle Statistics">
              <IconButton
                onClick={() => setShowStats(!showStats)}
                color={showStats ? "primary" : "default"}
                sx={{ borderRadius: 2, border: "1px solid rgba(0,0,0,0.1)" }}
              >
                <AnalyticsRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset All">
              <IconButton
                onClick={handleReset}
                sx={{ borderRadius: 2, border: "1px solid rgba(0,0,0,0.1)" }}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Statistics Cards */}
        {showStats && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total Jobs"
                value={stats.total}
                icon={EventRoundedIcon}
                color="#3f51b5"
                trend={stats.total > 0 ? "+Active" : "—"}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Date Range"
                value={stats.rangeText === "—" ? "—" : stats.rangeText}
                icon={CalendarTodayRoundedIcon}
                color="#1976d2"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Avg Per Day"
                value={stats.avgPerDay}
                icon={AccessTimeRoundedIcon}
                color="#388e3c"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Selected"
                value={selectedRows.size}
                icon={CheckCircleOutlineRoundedIcon}
                color={selectedRows.size > 0 ? "#d32f2f" : "#999"}
              />
            </Grid>
          </Grid>
        )}
      </Stack>

      {/* Filters Section */}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden" }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          {/* Filter Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: expandFilters ? 2 : 0, cursor: "pointer" }}
            onClick={() => setExpandFilters(!expandFilters)}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <TuneRoundedIcon sx={{ color: "#3f51b5" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Filters & Search
              </Typography>
            </Stack>
            <ArrowDropDownRoundedIcon
              sx={{
                transition: "transform 200ms ease",
                transform: expandFilters ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Stack>

          <Collapse in={expandFilters}>
            <Grid container spacing={2}>
              {/* Account */}
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Account <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                {loadingCompanies ? (
                  <Skeleton variant="rounded" height={48} />
                ) : (
                  <Select
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    value={{ value: companyName, label: companyName ? companyName : "Select Account" }}
                    options={companyNameOptions}
                    onChange={(opt) => setCompanyName(opt?.value || "")}
                    placeholder="Select Account"
                    isSearchable
                  />
                )}
              </Grid>

              {/* Location */}
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Location <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                {loadingLocations ? (
                  <Skeleton variant="rounded" height={48} />
                ) : (
                  <Select
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    value={{ value: locationName, label: locationName ? locationName : "Select Location" }}
                    options={locationNameOptions}
                    onChange={(opt) => setLocationName(opt?.value || "")}
                    placeholder={companyName ? "Select Location" : "Select account first"}
                    isDisabled={!companyName}
                    isSearchable
                  />
                )}
              </Grid>

              {/* Date Range */}
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Date Range <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <RangePicker
                  style={{ width: "100%", height: 48, borderRadius: 12 }}
                  format="MM-DD-YYYY"
                  value={fromDate && toDate ? [fromDate, toDate] : []}
                  onChange={(vals) => {
                    if (!vals || vals.length !== 2) {
                      setFromDate(null);
                      setToDate(null);
                      return;
                    }
                    setFromDate(vals[0]);
                    setToDate(vals[1]);
                  }}
                  disabled={!companyName || !locationName}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
              </Grid>

              {/* Fetch Button */}
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, visibility: "hidden" }}>
                  Action
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleFetch}
                  disabled={!companyName || !locationName || !fromDate || !toDate || loadingTable}
                  sx={{
                    height: 48,
                    borderRadius: 2,
                    fontWeight: 900,
                    width: "100%",
                    background: loadingTable
                      ? "rgba(63,81,181,0.6)"
                      : "linear-gradient(135deg, #3f51b5 0%, #1a237e 100%)",
                  }}
                >
                  {loadingTable ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Spin size="small" />
                      <span>Fetching...</span>
                    </Stack>
                  ) : (
                    "Fetch Jobs"
                  )}
                </Button>
              </Grid>

              {/* Divider */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Search + Sort */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search automation name, location, creator..."
                  disabled={!originalData?.length}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ display: "flex", alignItems: "center", mr: 1, opacity: 0.6 }}>
                        <SearchRoundedIcon fontSize="small" />
                      </Box>
                    ),
                    sx: { height: 48, borderRadius: 2 },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Select
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  value={{ value: selectedSort, label: selectedSort }}
                  options={[
                    { value: "newest", label: "Newest First" },
                    { value: "oldest", label: "Oldest First" },
                    { value: "nameAZ", label: "Name (A-Z)" },
                    { value: "nameZA", label: "Name (Z-A)" },
                  ]}
                  onChange={(opt) => setSelectedSort(opt?.value)}
                  placeholder="Sort by..."
                  isSearchable={false}
                />
              </Grid>
            </Grid>

            {/* Help Text */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2.5 }} alignItems="center">
              <Alert severity="info" sx={{ flex: 1, borderRadius: 2 }}>
                <Typography variant="body2">
                  💡 Tip: Use search to filter jobs by name, location, creator, or date. Use the sort dropdown to organize results.
                </Typography>
              </Alert>
              <Chip
                label={`${tableData.length ? indexOfFirstRow + 1 : 0}–${Math.min(
                  indexOfLastRow,
                  tableData.length
                )} of ${tableData.length}`}
                variant="outlined"
                sx={{ whiteSpace: "nowrap" }}
              />
            </Stack>
          </Collapse>
        </CardContent>
      </Card>

      {/* Results Section */}
      <Box sx={{ mt: 3 }}>
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}>
          <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
            {/* Title Bar */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Jobs Results
                </Typography>
                {tableData.length > 0 && (
                  <Chip label={`${tableData.length} total`} size="small" variant="outlined" />
                )}
              </Stack>

              {loadingTable && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Spin />
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Loading…
                  </Typography>
                </Stack>
              )}
            </Stack>

            {/* Bulk Actions */}
            {tableData.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: "rgba(63,81,181,0.05)",
                  border: "1px solid rgba(63,81,181,0.15)",
                }}
              >
                <Switch
                  size="small"
                  checked={bulkSelectMode}
                  onChange={(e) => {
                    setBulkSelectMode(e.target.checked);
                    if (!e.target.checked) setSelectedRows(new Set());
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Bulk Select
                </Typography>
                {selectedRows.size > 0 && (
                  <>
                    <Divider orientation="vertical" sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#d32f2f" }}>
                      {selectedRows.size} selected
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<FileDownloadRoundedIcon />}
                      onClick={handleBulkDownload}
                      sx={{ borderRadius: 2, fontWeight: 900 }}
                    >
                      Download All
                    </Button>
                  </>
                )}
              </Stack>
            )}

            {/* Table */}
            {!loadingTable && !tableData?.length ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <Empty
                  description={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        No jobs found
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Select filters and click <b>Fetch Jobs</b> to get started
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, overflowX: "auto" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {bulkSelectMode && (
                          <TableCell
                            padding="checkbox"
                            sx={{
                              fontWeight: 900,
                              backgroundColor: "rgba(0,0,0,0.04)",
                              borderBottom: "1px solid rgba(0,0,0,0.08)",
                            }}
                          >
                            <Tooltip title="Select all on this page">
                              <span></span>
                            </Tooltip>
                          </TableCell>
                        )}
                        {["S.No", "Location", "Automation", "Created", "By", "Actions"].map((h) => (
                          <TableCell
                            key={h}
                            align="center"
                            sx={{
                              fontWeight: 900,
                              py: 1.5,
                              backgroundColor: "rgba(0,0,0,0.04)",
                              borderBottom: "1px solid rgba(0,0,0,0.08)",
                              fontSize: "0.85rem",
                              letterSpacing: 0.3,
                            }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {currentRows.map((row, idx) => {
                        const globalIdx = indexOfFirstRow + idx;
                        const isSelected = selectedRows.has(globalIdx);

                        return (
                          <TableRow
                            key={globalIdx}
                            hover
                            selected={isSelected}
                            sx={{
                              "& td": { py: 1.3 },
                              transition: "all 150ms ease",
                              backgroundColor: isSelected ? "rgba(63,81,181,0.08)" : "transparent",
                              "&:hover": {
                                backgroundColor: isSelected ? "rgba(63,81,181,0.12)" : "rgba(63,81,181,0.04)",
                                transform: "scale(1.001)",
                              },
                            }}
                          >
                            {bulkSelectMode && (
                              <TableCell padding="checkbox">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const newSet = new Set(selectedRows);
                                    if (e.target.checked) {
                                      newSet.add(globalIdx);
                                    } else {
                                      newSet.delete(globalIdx);
                                    }
                                    setSelectedRows(newSet);
                                  }}
                                />
                              </TableCell>
                            )}
                            <TableCell align="center" sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
                              {globalIdx + 1}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: "0.9rem" }}>
                              <Chip label={row?.location_name || "—"} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                              {row?.automationName || "—"}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: "0.85rem", opacity: 0.8 }}>
                              <Tooltip title={row?.created_on || row?.created_date || "—"}>
                                <span>{moment(row?.created_on || row?.created_date).fromNow()}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: "0.9rem" }}>
                              {row?.created_by || "—"}
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                <Tooltip title="View Details">
                                  <IconButton
                                    size="small"
                                    onClick={() => openView(row)}
                                    sx={{
                                      borderRadius: 1.5,
                                      border: "1px solid rgba(63,81,181,0.3)",
                                      backgroundColor: "rgba(63,81,181,0.05)",
                                      "&:hover": { backgroundColor: "rgba(63,81,181,0.15)" },
                                    }}
                                  >
                                    <VisibilityRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Download CSV">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadClick(row)}
                                    sx={{
                                      borderRadius: 1.5,
                                      border: "1px solid rgba(76,175,80,0.3)",
                                      backgroundColor: "rgba(76,175,80,0.05)",
                                      "&:hover": { backgroundColor: "rgba(76,175,80,0.15)" },
                                    }}
                                  >
                                    <DownloadRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 3 }}
                >
                  <Typography variant="body2" sx={{ opacity: 0.75 }}>
                    Showing <b>{tableData.length ? indexOfFirstRow + 1 : 0}</b>-
                    <b>{Math.min(indexOfLastRow, tableData.length)}</b> of <b>{tableData.length}</b> jobs
                  </Typography>

                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    shape="rounded"
                    color="primary"
                    siblingCount={1}
                    boundaryCount={1}
                    disabled={loadingTable}
                  />
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={closeView} fullWidth maxWidth="lg" scroll="paper">
        <DialogTitle sx={{ pb: 1, background: "linear-gradient(135deg, #3f51b5 0%, #1a237e 100%)" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box sx={{ color: "white" }}>
              <Typography variant="h6" sx={{ fontWeight: 900, color: "white" }}>
                Job Details
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {viewRow?.automationName || "—"} • {viewRow?.location_name || "—"}
              </Typography>
            </Box>
            <IconButton onClick={closeView} sx={{ color: "white" }}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          <Tabs
            value={viewTab}
            onChange={(_, v) => setViewTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 2 }}
          >
            <Tab label="Summary" icon={<EventRoundedIcon />} iconPosition="start" />
            <Tab label="Response Details" icon={<AnalyticsRoundedIcon />} iconPosition="start" />
          </Tabs>

          {/* Summary Tab */}
          <TabPanel value={viewTab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid rgba(0,0,0,0.08)",
                    background: "linear-gradient(135deg, rgba(63,81,181,0.05) 0%, transparent 100%)",
                  }}
                >
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700 }}>
                          LOCATION
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.5 }}>
                          {viewRow?.location_name || "—"}
                        </Typography>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700 }}>
                          AUTOMATION NAME
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.5 }}>
                          {viewRow?.automationName || "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid rgba(0,0,0,0.08)",
                    background: "linear-gradient(135deg, rgba(25,118,210,0.05) 0%, transparent 100%)",
                  }}
                >
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700 }}>
                          CREATED ON
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.5 }}>
                          {moment(viewRow?.created_on || viewRow?.created_date).format("MMM DD, YYYY HH:mm") || "—"}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.6, display: "block", mt: 0.5 }}>
                          {moment(viewRow?.created_on || viewRow?.created_date).fromNow()}
                        </Typography>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700 }}>
                          CREATED BY
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.5 }}>
                          {viewRow?.created_by || "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Response Info Tab */}
          <TabPanel value={viewTab} index={1}>
            {!responseRows.length ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Empty description="No response data available for this job." />
              </Box>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    overflowX: "auto",
                    overflowY: "auto",
                    maxHeight: 600,
                    border: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
                    <TableHead>
                      <TableRow>
                        {responseCols.map((c) => (
                          <TableCell
                            key={c}
                            align="center"
                            sx={{
                              fontWeight: 900,
                              py: 1.3,
                              backgroundColor: "rgba(63,81,181,0.08)",
                              borderBottom: "2px solid rgba(0,0,0,0.1)",
                              whiteSpace: "nowrap",
                              fontSize: "0.85rem",
                            }}
                          >
                            {getFriendlyHeader(c)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {responseRows.slice(0, 500).map((r, i) => (
                        <TableRow key={i} hover sx={{ "&:hover": { backgroundColor: "rgba(63,81,181,0.04)" } }}>
                          {responseCols.map((c) => (
                            <TableCell
                              key={c}
                              align="center"
                              sx={{
                                py: 1.2,
                                whiteSpace: "nowrap",
                                fontSize: "0.85rem",
                                maxWidth: 300,
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            >
                              {r?.[c] === undefined || r?.[c] === null || r?.[c] === "" ? (
                                <Typography variant="body2" sx={{ opacity: 0.5 }}>
                                  —
                                </Typography>
                              ) : (
                                <Tooltip title={String(r[c])}>
                                  <span>{String(r[c])}</span>
                                </Tooltip>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {responseRows.length > 500 && (
                  <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                    Showing first 500 rows. Download CSV to access complete data.
                  </Alert>
                )}
              </>
            )}
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 2, background: "rgba(0,0,0,0.02)" }}>
          <Button onClick={closeView} variant="outlined" sx={{ borderRadius: 2, fontWeight: 900 }}>
            Close
          </Button>
          <Button
            onClick={() => handleDownloadClick(viewRow)}
            variant="contained"
            startIcon={<DownloadRoundedIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
            }}
          >
            Download CSV
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
